import {RU} from "@/i18n/ru";
import {Lang, LangDescription, LangProp, LocationType} from "@/store/player/types";
import {EN} from "@/i18n/en";
import {IntegrationUtils} from "@/utils/integration-utils";
import PlayerStore from "@/store/player";
import {NativeBootstrapContext} from "@/model/routing";
import {StandUrls} from "@/utils/stand-urls";

export abstract class Settings {

    public static getVersion(): number {
        if (window.location.href.indexOf("startVersion=") != -1) {
            const match = window.location.href.match(/startVersion=(\d+)/);
            if (match) {
                const parsed = parseInt(match[1], 10);
                if (parsed >= 3 && parsed <= 18) {
                    return parsed;
                }
            }
        }
        return 18;
        // const versions = [4, 17];
        // return versions[Math.floor(Math.random() * versions.length)];
    }

    public static readonly DEFAULT_START_VERSION = Settings.getVersion();

    public static readonly MAX_CREATURES = 6;

    // Начиная с этой стартовой версии клики по меню запрещены, пока показывается обучалка.
    public static readonly EDU_MENU_BLOCK_MIN_START_VERSION = 16;

    public static readonly MUSIC_VOLUME: number = 0.3;
    public static readonly SHORT_TRACK_VOLUME: number = 0.3;

    public static readonly ARENA_MIN_LEVEL_TO_SHOW_BUTTON: number = 4;
    public static readonly ARENA_MIN_LEVEL: number = 8;

    public static readonly CLAN_MIN_LEVEL_TO_SHOW_BUTTON: number = 8;
    public static readonly CLAN_MIN_LEVEL: number = 15;

    public static readonly MAIN_CHAT_ROOM: string = 'MAIN';
    public static readonly CLAN_CHAT_ROOM: string = 'CLAN';
    public static readonly PRIVAT_CHAT_ROOM: string = 'PRIVAT';
    
    public static readonly FORWARDED_MESSAGE_PREFIX: string = "4682d7c4-f542-4385-9e0a-6edf74e8aa5b";
    public static readonly SMILEY_MESSAGE_PREFIX: string = "f5570448-1e96-4f5e-973d-e37dfbe2ceb4...";
    public static readonly CHAT_QUOTE_ENABLED: boolean = false;

    public static readonly MAX_MESSAGE_LENGTH: number = 1000;

    public static readonly MIN_ELIXIR_SETTINGS_STAIR = 25;
    public static readonly MIN_BACKPACK_STAIR = 1;

    public static readonly MIN_GIFTS_LEVEL = 15;

    public static readonly LEGEND_HERO_MIN_STAIR = 30;
    public static readonly MYTH_HERO_MIN_STAIR = 100;
    public static readonly TOP_HERO_MIN_STAIR = 500;

    public static readonly CLAN_THEME_FREE_CHANGES: number = 3;
    public static readonly CLAN_THEME_CHANGE_PRICE: number = 250;

    public static LANG_TYPE = Lang.RU;
    public static LANG: any = RU;

    public static MUSIC_LOADED: boolean = false;

    public static CURRENCY = 'RUB';

    public static LOGGED_LOAD_FIVE: boolean = false;
    public static LOGGED_LOAD_HALF: boolean = false;
    public static LOGGED_GAME_STARTED: boolean = false;

    public static KNIGHT_OFFSET_X: number = 0;

    public static LOCATION: LocationType = LocationType.RU;

    public static AVAILABLE_LANGUAGES: LangProp[] = [];

    public static NUN_MIN_STAIR = 3;

    public static readonly LOCK_HERO_MIN_STAIR = 100;

    public static setEn() {
        Settings.applyUiLang(Lang.EN);
    }

    public static setRu() {
        Settings.applyUiLang(Lang.RU);
    }

    /** Словарь интерфейса. Новый язык — одна запись здесь и файл i18n, без правок смены языка. */
    private static readonly UI_PACKS: Partial<Record<Lang, typeof RU>> = {
        [Lang.RU]: RU,
        [Lang.EN]: EN,
    };

    public static applyUiLang(lang: Lang) {
        const pack = Settings.UI_PACKS[lang];
        if (pack) {
            Settings.LANG = pack;
            Settings.LANG_TYPE = lang;
            return;
        }
        Settings.LANG = EN;
        Settings.LANG_TYPE = Lang.EN;
    }

    /** Стартовый логотип в index.html читает это значение до загрузки профиля. */
    public static readonly UI_LANG_STORAGE_KEY = 'aof_ui_lang';

    public static storedUiLang(): Lang | null {
        try {
            const value = window.localStorage.getItem(Settings.UI_LANG_STORAGE_KEY);
            if (value === Lang.EN || value === Lang.RU) {
                return value;
            }
        } catch (e) {
            // private mode
        }
        return null;
    }

    public static rememberUiLang(): void {
        const code = Settings.LANG_TYPE === Lang.EN ? Lang.EN : Lang.RU;
        try {
            window.localStorage.setItem(Settings.UI_LANG_STORAGE_KEY, code);
        } catch (e) {
            // private mode
        }
        const logo = document.getElementById('splash-logo') as HTMLImageElement | null;
        const bg = document.getElementById('bg');
        if (!logo || !bg || bg.style.display === 'none') {
            return;
        }
        const src = code === Lang.EN ? 'assets/ui/logo3_en.webp' : 'assets/ui/logo3.webp';
        if (logo.getAttribute('src') !== src) {
            logo.src = src;
        }
    }

    public static playerLang(code: string): Lang {
        const upper = code.toUpperCase();
        const values = Object.values(Lang) as string[];
        if (values.includes(upper) && upper !== Lang.ANY) {
            return upper as Lang;
        }
        return Lang.EN;
    }

    public static playableLangs(): Lang[] {
        return (Object.values(Lang) as Lang[]).filter(lang => lang !== Lang.ANY && Settings.UI_PACKS[lang]);
    }

    public static applyYandexSdkLang(sdkLang: string | null | undefined): void {
        if (!sdkLang) {
            return;
        }

        const normalizedLang = sdkLang.toLowerCase();
        if (normalizedLang.startsWith('en')) {
            Settings.setEn();
        } else if (normalizedLang.startsWith('ru')) {
            Settings.setRu();
        }
    }

    /** DBG overlay: why UI language was chosen. */
    public static logLangChoice(lang: string, reason: string): void {
        StandUrls.debugLog(`[Lang] UI=${lang} — ${reason}`);
    }

    public static setLangDefaults(): void {
        if (IntegrationUtils.isUnifiedLangFlowEnabled()) {
            Settings.setLangDefaultsUnified();
        } else {
            Settings.setLangDefaultsLegacy();
        }

        Object.keys(LangDescription).forEach((key, index) => {
            let langProp = new LangProp();
            langProp.langId = key;
            langProp.langDescr = Object.values(LangDescription)[index];
            langProp.available = true;
            this.AVAILABLE_LANGUAGES.push(langProp);
        })

        PlayerStore.setRemindCampaignRules(true);
    }

    /** Legacy web defaults — keep behavior 1:1 with pre-AOF-2940. */
    private static setLangDefaultsLegacy(): void {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        let lang = urlParams.get('lang');

        // if (!lang && process.env.VUE_APP_IS_MOBILE_APP) {
        //     var locationService = new LocationService();
        //     locationService.getIp().then((ipDto: LocationIpDto) => {
        //             locationService.getLocationDataByIp(ipDto.ip).then((locationDataDto: LocationDataDto) => {
        //                 if (locationDataDto.country_code2 == "RU") {
        //                     Settings.LOCATION = LocationType.RU;
        //                 }
        //             })
        //         }
        //     );
        // }

        if (IntegrationUtils.isCrazyUser() || IntegrationUtils.isKongUser() || window.location.href.includes('mirland.mobi') || window.location.href.includes('en.lgtest.fun')) {
            Settings.setEn();
            Settings.logLangChoice('EN', `legacy: Crazy/Kong or host ${window.location.hostname}`);
        } else if (IntegrationUtils.isYandexUser()) {
            const yandexSdkLang = (window as any).ysdk?.environment?.i18n?.lang;
            if (yandexSdkLang) {
                Settings.applyYandexSdkLang(yandexSdkLang);
                Settings.logLangChoice(String(Settings.LANG_TYPE), `legacy: Yandex SDK i18n.lang=${yandexSdkLang}`);
            } else if (window.location.href.includes('yandex.com') && !window.location.href.includes('ru') && lang !== 'ru') {
                Settings.setEn();
                Settings.logLangChoice('EN', 'legacy: yandex.com without ru');
            } else if (lang === 'en') {
                Settings.setEn();
                Settings.logLangChoice('EN', 'legacy: Yandex ?lang=en');
            } else {
                Settings.setRu();
                Settings.logLangChoice('RU', 'legacy: Yandex default');
            }
        } else if (IntegrationUtils.isI18NEnabled()) {
            if (((lang && lang.includes('en')) || queryString.includes('lglass.mobi')) ||
                queryString.includes('mirland.mobi') || window.location.hostname.includes('mirland.mobi')) {
                Settings.setEn();
                Settings.logLangChoice('EN', `legacy: i18n host/query lang=${lang || '-'} host=${window.location.hostname}`);
            } else if (lang !== 'ru' && Settings.storedUiLang() === Lang.EN) {
                Settings.setEn();
                Settings.logLangChoice('EN', 'legacy: stored aof_ui_lang');
            } else {
                console.log('ru = ' + queryString);
                Settings.setRu();
                Settings.logLangChoice('RU', `legacy: i18n default host=${window.location.hostname}`);
            }

            if (IntegrationUtils.isMystoreUser()) {
                const curr = urlParams.get('currency');
                if (curr) {
                    Settings.CURRENCY = curr;
                }
            }
        }
    }

    /**
     * Native-app (unified) defaults:
     * 1) ?lang=  2) native deviceLocale / defaultLang  3) host shard  4) navigator.language  5) fallback
     */
    private static setLangDefaultsUnified(): void {
        const native = typeof window !== 'undefined'
            ? (window as Window & { __AOF_NATIVE_BOOTSTRAP__?: NativeBootstrapContext }).__AOF_NATIVE_BOOTSTRAP__
            : undefined;
        StandUrls.debugLog(
            `[Lang] unified inputs: host=${typeof window !== 'undefined' ? window.location.hostname : '-'}`
            + `, ?lang=${new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('lang') || '-'}`
            + `, native.defaultLang=${native?.defaultLang || '-'}`
            + `, native.deviceLocale=${native?.deviceLocale || '-'}`
            + `, native.storeCountry=${native?.storeCountry || '-'}`
            + `, native.routingReason=${native?.routingReason || '-'}`
            + `, native.serverType=${native?.serverType || '-'}`
            + `, navigator.language=${typeof navigator !== 'undefined' ? navigator.language : '-'}`,
        );

        const queryLang = new URLSearchParams(window.location.search).get('lang');
        if (Settings.applyLangHint(queryLang)) {
            Settings.logLangChoice(String(Settings.LANG_TYPE), `URL ?lang=${queryLang}`);
            return;
        }

        if (native?.defaultLang === 'EN' || native?.defaultLang === 'RU') {
            if (native.defaultLang === 'EN') {
                Settings.setEn();
            } else {
                Settings.setRu();
            }
            Settings.logLangChoice(
                native.defaultLang,
                Settings.explainBootstrapLang(native.routingReason, native.deviceLocale, native.storeCountry, native.serverType, native.defaultLang),
            );
            return;
        }
        if (Settings.applyLangHint(native?.deviceLocale)) {
            Settings.logLangChoice(String(Settings.LANG_TYPE), `native deviceLocale=${native?.deviceLocale} (no defaultLang yet)`);
            return;
        }

        if (IntegrationUtils.isForeignShardHost()) {
            Settings.setEn();
            Settings.logLangChoice('EN', `host ${window.location.hostname} is FOREIGN shard`);
            return;
        }
        if (IntegrationUtils.isRuShardHost()) {
            Settings.setRu();
            Settings.logLangChoice('RU', `host ${window.location.hostname} is RU shard`);
            return;
        }

        const nav = typeof navigator !== 'undefined' ? navigator.language : undefined;
        if (Settings.applyLangHint(nav)) {
            Settings.logLangChoice(String(Settings.LANG_TYPE), `navigator.language=${nav}`);
            return;
        }

        Settings.setRu();
        Settings.logLangChoice('RU', `fallback (deviceLocale=${native?.deviceLocale || nav || '-'} is not ru/en)`);
    }

    public static explainBootstrapLang(
        routingReason: string | undefined,
        deviceLocale: string | undefined,
        storeCountry: string | undefined,
        serverType: string | undefined,
        defaultLang: string | undefined,
    ): string {
        const loc = deviceLocale || '-';
        const country = storeCountry || '-';
        const lang = defaultLang || '-';
        switch (routingReason) {
            case 'ACCOUNT_LOCATOR':
                return `ACCOUNT_LOCATOR: existing account on ${serverType || '-'} shard; UI ${lang} from deviceLocale=${loc} (or shard if locale is not ru/en)`;
            case 'DEVICE_LOCALE':
                return `DEVICE_LOCALE: deviceLocale=${loc} is ru or en → UI ${lang} / world ${serverType || '-'}`;
            case 'STORE_COUNTRY':
                return `STORE_COUNTRY: deviceLocale=${loc} is not ru/en; App Store/Play country ${country} → UI ${lang} / world ${serverType || '-'}`;
            case 'DEFAULT_EN':
                return `DEFAULT_EN: deviceLocale=${loc} is not ru/en and no mapping for storeCountry=${country} → EN / FOREIGN`;
            default:
                return `native defaultLang=${lang} reason=${routingReason || '-'} locale=${loc} storeCountry=${country}`;
        }
    }

    /** @returns true if a lang was applied from the hint */
    private static applyLangHint(hint: string | null | undefined): boolean {
        if (!hint) {
            return false;
        }
        const normalized = hint.trim().toLowerCase().replace(/_/g, '-');
        if (normalized === 'en' || normalized.startsWith('en-') || normalized.includes('en')) {
            Settings.setEn();
            return true;
        }
        if (normalized === 'ru' || normalized.startsWith('ru-')) {
            Settings.setRu();
            return true;
        }
        return false;
    }
}
